import { useRouter as useVueRouter } from 'vue-router'
import {
  SUPPORT_SESSION_ROUTE_QUERY_KEY,
  useSupportSession
} from '../../support-session'
import { type AppRoute } from '../route-definitions'
import type { RouterService } from '../router-symbol'
import type { GatRouter, GatRouteLocation } from '../router-types'
import { useRouterService } from './useRouterService'
import type { AiExtractDataResponse } from '@gat-ship/types'

export const ROUTE_PAYLOAD_STORAGE_KEY = 'gs-route-payload' as const

/** Extends the vue-router. Add any missing functionallity if you need it. */
export function useRouter(): Omit<GatRouter, 'install'> &
  RouterService & {
    openInNewTab: (to: GatRouteLocation<AppRoute>) => void
    openSmartPasteTab: (
      to: GatRouteLocation<AppRoute>,
      payload: AiExtractDataResponse
    ) => void
    getHref: (to: GatRouteLocation<AppRoute>) => string
  } {
  const router = useVueRouter()

  const routerService = useRouterService()

  const { isSupportSession, getSupportSessionId } = useSupportSession({
    inject: false
  })

  function openInNewTab<T extends AppRoute>(to: GatRouteLocation<T>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _to = to as any
    if (!_to.query) {
      _to.query = {}
    }
    if (isSupportSession.value) {
      _to.query[SUPPORT_SESSION_ROUTE_QUERY_KEY] = getSupportSessionId()
    }
    const routeData = router.resolve(to)

    window.open(routeData.href, '_blank')
  }

  function getHref<T extends AppRoute>(to: GatRouteLocation<T>) {
    const routeData = router.resolve(to)
    return routeData.href
  }

  function push<T extends AppRoute>(to: GatRouteLocation<T>) {
    return router.push(to)
  }

  function replace<T extends AppRoute>(to: GatRouteLocation<T>) {
    return router.replace(to)
  }

  function back() {
    router.back()
  }

  function forward() {
    router.forward()
  }

  function go(delta: number) {
    router.go(delta)
  }

  function resolve<T extends AppRoute>(to: GatRouteLocation<T>) {
    return router.resolve(to as any)
  }

  function addRoute(parentOrRoute: any, route?: any) {
    return router.addRoute(parentOrRoute, route)
  }

  function removeRoute(name: string) {
    return router.removeRoute(name)
  }

  function hasRoute(name: string) {
    return router.hasRoute(name)
  }

  function getRoutes() {
    return router.getRoutes()
  }

  function beforeEach(guard: any) {
    return router.beforeEach(guard)
  }

  function beforeResolve(guard: any) {
    return router.beforeResolve(guard)
  }

  function afterEach(guard: any) {
    return router.afterEach(guard)
  }

  function onError(handler: any) {
    return router.onError(handler)
  }

  function isReady() {
    return router.isReady()
  }

  function clearRoutes() {
    return router.clearRoutes()
  }

  function openSmartPasteTab<T extends AppRoute>(
    to: GatRouteLocation<T>,
    payload: AiExtractDataResponse
  ) {
    localStorage.setItem(ROUTE_PAYLOAD_STORAGE_KEY, JSON.stringify(payload))
    return openInNewTab(to)
  }

  return {
    // Router methods
    push,
    replace,
    back,
    forward,
    go,
    resolve,
    addRoute,
    removeRoute,
    hasRoute,
    getRoutes,
    beforeEach,
    beforeResolve,
    afterEach,
    onError,
    isReady,
    clearRoutes,
    getHref,
    openInNewTab,
    openSmartPasteTab,
    // Router properties
    currentRoute: router.currentRoute,
    options: router.options,
    listening: router.listening,

    // Router service
    ...routerService
  }
}
